import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { ArticleRow } from "@/components/ArticleRow";

export default function News() {
        const [articles, setArticles] = useState([]);
        const [parties, setParties] = useState([]);
        const [movements, setMovements] = useState([]);
        const [outlets, setOutlets] = useState([]);
        const [filters, setFilters] = useState({
                search: "",
                party: "",
                movement: "",
                outlet: "",
        });
        const [loading, setLoading] = useState(true);

        useEffect(() => {
                api.get("/parties").then((r) => setParties(r.data));
                api.get("/movements").then((r) => setMovements(r.data));
                api.get("/outlets").then((r) => setOutlets(r.data));
        }, []);

        useEffect(() => {
                let cancelled = false;
                setLoading(true);
                const params = { limit: 30 };
                Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
                api.get("/articles", { params })
                        .then((r) => !cancelled && setArticles(r.data))
                        .catch(() => !cancelled && setArticles([]))
                        .finally(() => !cancelled && setLoading(false));
                return () => {
                        cancelled = true;
                };
        }, [filters]);

        const update = (k, v) => setFilters((f) => ({ ...f, [k]: v === f[k] ? "" : v }));

        return (
                <main>
                        <section className="border-b border-[var(--line-strong)] relative overflow-hidden">
                                <div className="absolute inset-0 crosshair-bg opacity-20 pointer-events-none" />
                                <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14 relative">
                                        <div className="overline text-[var(--ink-faint)] mb-4 flex items-center gap-2">
                                                <span
                                                        className="pulse-class"
                                                        style={{ color: "var(--deception)" }}
                                                >
                                                        ●
                                                </span>
                                                Indexador ao vivo · feeds RSS portugueses
                                        </div>
                                        <h1 className="font-serif text-6xl md:text-7xl tracking-tighter leading-[0.95] text-[var(--ink)]">
                                                Dossiês{" "}
                                                <span className="italic text-[var(--controversy)]">
                                                        ao vivo.
                                                </span>
                                        </h1>
                                        <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[var(--ink-soft)] font-mono">
                                                Os 30 artigos mais recentes da imprensa portuguesa,
                                                cruzados por partido, movimento e órgão de comunicação.
                                                Use os filtros abaixo para isolar a controvérsia.
                                        </p>
                                </div>
                        </section>

                        <section className="border-b border-[var(--line-strong)] bg-[var(--bg-deep)]">
                                <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-8 space-y-6">
                                        <div className="flex items-center gap-3 max-w-md">
                                                <Search
                                                        size={14}
                                                        strokeWidth={1.5}
                                                        className="text-[var(--ink-faint)]"
                                                />
                                                <input
                                                        data-testid="news-search-input"
                                                        value={filters.search}
                                                        onChange={(e) =>
                                                                setFilters((f) => ({
                                                                        ...f,
                                                                        search: e.target.value,
                                                                }))
                                                        }
                                                        placeholder="Procurar nos artigos..."
                                                        className="flex-1 border bg-transparent px-3 py-2 font-mono text-sm focus:outline-none focus:border-[var(--controversy)]"
                                                        style={{ borderColor: "var(--line-strong)" }}
                                                />
                                        </div>

                                        <FilterRow
                                                label="Partido"
                                                items={parties.map((p) => p.key)}
                                                active={filters.party}
                                                onClick={(v) => update("party", v)}
                                                color="var(--controversy)"
                                                testIdPrefix="filter-party"
                                        />
                                        <FilterRow
                                                label="Movimento"
                                                items={movements.map((m) => m.key)}
                                                active={filters.movement}
                                                onClick={(v) => update("movement", v)}
                                                color="var(--truth)"
                                                testIdPrefix="filter-movement"
                                        />
                                        <FilterRow
                                                label="Órgão"
                                                items={outlets.map((o) => o.name)}
                                                values={outlets.map((o) => o.slug)}
                                                active={filters.outlet}
                                                onClick={(v) => update("outlet", v)}
                                                color="var(--ai)"
                                                testIdPrefix="filter-outlet"
                                        />
                                </div>
                        </section>

                        <section
                                className="mx-auto max-w-[1400px] px-6 lg:px-12 py-10"
                                data-testid="news-list"
                        >
                                {loading ? (
                                        <p className="font-mono text-sm text-[var(--ink-faint)] text-center py-12">
                                                A descodificar feeds...
                                        </p>
                                ) : articles.length === 0 ? (
                                        <div className="py-20 text-center">
                                                <p className="font-serif text-3xl text-[var(--ink)]">
                                                        Sem artigos para este filtro.
                                                </p>
                                                <p className="text-[var(--ink-soft)] mt-2 font-mono text-sm">
                                                        Tente outro partido / órgão ou limpe a pesquisa.
                                                </p>
                                        </div>
                                ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                                                {articles.map((a, i) => (
                                                        <ArticleRow key={a.id} article={a} index={i} />
                                                ))}
                                        </div>
                                )}
                        </section>
                </main>
        );
}

const FilterRow = ({ label, items, values, active, onClick, color, testIdPrefix }) => (
        <div className="flex items-center gap-3 flex-wrap">
                <span
                        className="overline w-24 flex-shrink-0"
                        style={{ color: "var(--ink-faint)" }}
                >
                        {label}
                </span>
                <div className="flex flex-wrap gap-1">
                        {items.map((it, i) => {
                                const val = values ? values[i] : it;
                                const a = active === val;
                                return (
                                        <button
                                                key={val}
                                                data-testid={`${testIdPrefix}-${val}`}
                                                onClick={() => onClick(val)}
                                                className="overline px-2 py-1 border transition-colors"
                                                style={{
                                                        borderColor: a ? color : "var(--line-strong)",
                                                        color: a ? color : "var(--ink-soft)",
                                                        background: a
                                                                ? "rgba(212, 160, 23, 0.06)"
                                                                : "transparent",
                                                }}
                                        >
                                                {it}
                                        </button>
                                );
                        })}
                </div>
        </div>
);
